import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { VerticalStack } from './Stack';

export interface AdminShellProps {
  /** The primary title or WorksurfaceHeader */
  title: ReactNode;
  /** Primary actions shown in the header */
  actions?: ReactNode;
  /** Main content area */
  children: ReactNode;
  /** Optional secondary info or help text */
  subtitle?: ReactNode;
  /** Additional styling for the root container */
  className?: string;
  /** Additional styling for the header area */
  headerClassName?: string;
  /** Additional styling for the content area */
  bodyClassName?: string;
  /** Anchor for product tours */
  tourAnchorId?: string;
  /** If true, removes default p-6 padding from the body */
  flat?: boolean;
}

/**
 * Silk UI AdminShell
 * A high-density detailed worksurface building block.
 * Follows the "Frame Before Flourish" philosophy: flat surfaces, clear borders, 16px rhythm.
 */
export function AdminShell({
  title,
  actions,
  children,
  subtitle,
  className,
  headerClassName,
  bodyClassName,
  tourAnchorId,
  flat = false,
}: AdminShellProps) {
  return (
    <div 
      className={cn("relative flex h-full min-h-0 w-full flex-col bg-background", className)}
      id={tourAnchorId}
      data-tour-anchor={tourAnchorId ? `anchor-${tourAnchorId}` : undefined}
    >
      {/* Worksurface Header Area */}
      <div className={cn(
        "flex shrink-0 flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-6 3xl:px-12 3xl:py-12 4k:px-16 4k:py-16 border-b border-border/40 bg-muted/5",
        headerClassName
      )}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 3xl:gap-8">
          <VerticalStack gap={1} className="sm:gap-2 3xl:gap-4">
            {typeof title === 'string' ? (
              <h1 className="text-lg sm:text-xl 3xl:text-3xl 4k:text-4xl font-bold tracking-tight text-foreground font-heading">
                {title}
              </h1>
            ) : title}
            {subtitle && (
              <div className="text-xs sm:text-sm 3xl:text-lg 4k:text-xl text-muted-foreground font-medium leading-relaxed max-w-2xl 3xl:max-w-4xl">
                {subtitle}
              </div>
            )}
          </VerticalStack>
          
          {actions && (
            <div className="flex items-center gap-1.5 sm:gap-2 3xl:gap-4 shrink-0">
              {actions}
            </div>
          )}
        </div>
      </div>

      {/* Main Worksurface Body */}
      <div className={cn(
        "min-h-0 flex-1",
        !flat && "p-3 sm:p-6 3xl:p-12 4k:p-16",
        bodyClassName
      )}>
        {children}
      </div>
    </div>
  );
}
